import { createPerfilApiClient, getPerfilCredentials, withPerfilCredentials } from './perfilApiClient';

/**
 * @typedef {{ id: number|string, nombre_completo: string, categoria?: string, n_plazos?: number, url_recibo?: string }} Deportista
 * @typedef {{ id?: number|string, importe?: number, importe_con_gd?: number, descuento_aplicado?: number, estado?: number, estado_string?: string, fecha?: string }} Plazo
 * @typedef {{ current_page: number, last_page: number, total: number, per_page: number }} PaginationMeta
 */

export default {
    /**
     * @param {{ page?: number, per_page?: number, filters?: { search?: string, lista_id?: number|string } }} params
     * @returns {Promise<{ items: Deportista[], meta: PaginationMeta }>}
     */
    async getDeportistas({ page = 1, per_page = 10, filters = {} } = {}) {
        try {
            const { token, hashTeatro } = getPerfilCredentials();
            const params = {
                page,
                per_page,
                api_key: token,
                ...(hashTeatro ? { tid: hashTeatro } : {}),
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.lista_id ? { lista_id: filters.lista_id } : {}),
            };
            const response = await createPerfilApiClient().get('/f/perfil/bases', { params });

            const data = response.data ?? {};
            const rawItems = data.data ?? data.bases ?? data.items ?? [];
            const items = Array.isArray(rawItems) ? rawItems : [];
            const meta = {
                current_page: data.current_page ?? data.meta?.current_page ?? 1,
                last_page: data.last_page ?? data.meta?.last_page ?? 1,
                total: data.total ?? data.meta?.total ?? items.length,
                per_page: data.per_page ?? data.meta?.per_page ?? items.length,
            };

            return { items, meta };
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[cantera] Error al obtener deportistas:', error);
            }
            throw error;
        }
    },

    /**
     * @param {number|string} id
     * @param {{ page?: number, per_page?: number }} params
     * @returns {Promise<Plazo[]>}
     */
    async getDeportistaDetalle(id, { page = 1, per_page = 50 } = {}) {
        try {
            const { token } = getPerfilCredentials();
            const response = await createPerfilApiClient().get(`/f/perfil/base/${id}/plazos`, {
                params: {
                    page,
                    per_page,
                    api_key: token,
                },
            });

            const data = response.data ?? {};
            const rawItems = data.data ?? data.plazos ?? data.items ?? [];
            return Array.isArray(rawItems) ? rawItems : [];
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(`[cantera] Error al obtener plazos de la base ${id}:`, error);
            }
            throw error;
        }
    },

    /**
     * Procesa el pago de plazos seleccionados.
     *
     * @param {Object} domainPayload — payload de dominio sin credenciales
     * @returns {Promise<void>}
     */
    async procesarPagoPlazos(domainPayload) {
        try {
            const { token, hashTeatro } = getPerfilCredentials();
            const payload = {
                tid: hashTeatro,
                ...domainPayload,
            };

            await createPerfilApiClient().post('/f/perfil/paycomet/procesar-pago-plazos', payload, {
                params: {
                    ...(token ? { api_key: token } : {}),
                },
            });
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[cantera] Error al procesar pago de plazos:', error);
            }
            throw error;
        }
    },

    /**
     * @param {string} url
     * @returns {string}
     */
    buildReciboUrl(url) {
        return withPerfilCredentials(url);
    },
};
